<template>
  <template v-if="isDeviceReady">
    <!-- PC端客服机器人 -->
    <div v-if="!isMobile">
      <div
        ref="botContainer"
        class="customer-service-bot"
        :class="{ 'is-dragging': isDragging }"
        :style="botPositionStyle"
        @mousedown="startDrag"
        @touchstart="startTouchDrag"
      >
        <!-- PC端功能按钮：按图一展示为常驻竖向工具条 -->
        <div class="bot-buttons">
          <ContactPopup>
            <template #trigger>
              <div class="bot-button-item bot-button-item--service">
                <span class="bot-avatar">
                  <img :src="withBase('/images/chatbot/avatar.png')" alt="客服" class="bot-icon" />
                </span>
                <span>客服</span>
              </div>
            </template>
          </ContactPopup>
          <ConsultantPopup
            position="left"
            :popup-container="botContainer"
            :popup-visible="panelPopupVisible"
            :scroll-to-close="true"
            :scroll-to-close-distance="SCROLL_CLOSE_THRESHOLD"
          >
            <template #trigger>
              <div class="bot-button-item">
                <SvgIcon class="bot-button-icon" name="consultant" size="20" />
                <span>顾问</span>
              </div>
            </template>
          </ConsultantPopup>
          <div class="bot-button-item" @click.stop="handleFunction('feedback')">
            <SvgIcon class="bot-button-icon" name="feedback" size="20" />
            <span>反馈</span>
          </div>
        </div>

        <BaseBackToTop
          class="bot-back-to-top"
          inline
          :visible="showBackToTop"
          :guard="canUseFloatingAction"
        />
      </div>
    </div>

    <!-- 移动端客服机器人 -->
    <MobileBot v-else />
  </template>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, nextTick } from 'vue'
import BaseBackToTop from '../BackToTop.vue'
import ConsultantPopup from '../consultant/ConsultantWrapper.vue'
import ContactPopup from '../contact/ContactPopupWrapper.vue'
import MobileBot from './MobileBot.vue'
import { useMediaQuery } from '@/composables/useMediaQuery'
import { throttle } from '@/utils'
import { openFeedbackModal } from '~/utils/feedbackModal'

const { withBase } = useBasePath()

const isDragging = ref(false)
const dragMoved = ref(false)
const suppressClickUntil = ref(0)
const showBackToTop = ref(false)
const isScrollClosingPanels = ref(false)
const scrollCloseTimer = ref(null)
const scrollCloseOriginY = ref(0)
let gsapInstance = null
let gsapLoadPromise = null
let isChatbotUnmounted = false

const loadGsap = async () => {
  if (!import.meta.client) return null
  if (gsapInstance) return gsapInstance
  gsapLoadPromise ??= import('gsap').then((module) => {
    gsapInstance = module.default || module.gsap
    return gsapInstance
  })
  return gsapLoadPromise
}

const animateBotScale = (scale, duration, ease) => {
  const target = botContainer.value
  if (!target) return
  void loadGsap()
    .then((gsap) => {
      if (!gsap || isChatbotUnmounted || !target.isConnected) return
      gsap.killTweensOf(target)
      gsap.to(target, { scale, duration, ease })
    })
    .catch(() => {})
}

const { isMobile } = useMediaQuery()

const isDeviceReady = ref(false)

const botContainer = ref(null)

const INITIAL_BOTTOM_PC = 80
const INITIAL_RIGHT_PC = 8
const MIN_MARGIN_TOP = 60
const MIN_MARGIN_BOTTOM = 8

const position = reactive({
  x: INITIAL_RIGHT_PC,
  y: INITIAL_BOTTOM_PC,
})

const dragStart = reactive({
  y: INITIAL_BOTTOM_PC,
})

const isTouchDragging = ref(false)
const touchDragStart = reactive({
  y: INITIAL_BOTTOM_PC,
})

const panelPopupVisible = computed(() =>
  isDragging.value || isTouchDragging.value || isScrollClosingPanels.value ? false : undefined
)

const DRAG_CLICK_SUPPRESS_MS = 250
const DRAG_MOVE_THRESHOLD = 3
const BACK_TO_TOP_THRESHOLD = 300
const SCROLL_CLOSE_THRESHOLD = 8

// 计算机器人位置样式
const botPositionStyle = computed(() => {
  return {
    top: `${position.y}px`,
    right: `${position.x}px`,
  }
})

// 初始化机器人位置：以工具条顶部为定位基准，避免回到顶部按钮出现时挤动工具条。
const initPosition = () => {
  if (!botContainer.value) return

  position.x = INITIAL_RIGHT_PC
  const toolbarHeight =
    botContainer.value.querySelector?.('.bot-buttons')?.offsetHeight ||
    botContainer.value.offsetHeight
  position.y = window.innerHeight - INITIAL_BOTTOM_PC - toolbarHeight
  updatePositionWithConstraints()
}

// 根据约束条件更新位置：垂直方向仅在视口内，距顶/底保留安全边距
const updatePositionWithConstraints = () => {
  if (!botContainer.value) return

  const windowHeight = window.innerHeight
  const botHeight = botContainer.value.offsetHeight

  const minTop = MIN_MARGIN_TOP
  const maxTop = Math.max(minTop, windowHeight - botHeight - MIN_MARGIN_BOTTOM)

  position.y = Math.min(Math.max(position.y, minTop), maxTop)
  position.x = INITIAL_RIGHT_PC
}

const shouldSuppressClick = () => Date.now() < suppressClickUntil.value
const canUseFloatingAction = () => !shouldSuppressClick()

const closeFloatingPanelsForDrag = () => {
  if (!import.meta.client) return
  document.activeElement?.blur?.()
  document.body.click()
}

const closeFloatingPanelsForScroll = () => {
  if (!import.meta.client) return
  isScrollClosingPanels.value = true
  document.activeElement?.blur?.()
  document.body.click()

  if (scrollCloseTimer.value) {
    window.clearTimeout(scrollCloseTimer.value)
  }

  scrollCloseTimer.value = window.setTimeout(() => {
    isScrollClosingPanels.value = false
    scrollCloseTimer.value = null
  }, 180)
}

// 开始拖拽
const startDrag = (e) => {
  if (e.button !== undefined && e.button !== 0) return

  // 防止事件冒泡和默认行为
  e.stopPropagation()
  e.preventDefault()

  isDragging.value = true
  dragMoved.value = false
  closeFloatingPanelsForDrag()
  dragStart.y = e.clientY

  // 移除之前可能存在的事件监听器（防止重复添加）
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)

  // 添加全局事件监听
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)

  animateBotScale(0.98, 0.15, 'power2.in')
}

// 开始触摸拖拽（移动端）
const startTouchDrag = (e) => {
  // 防止事件冒泡
  e.stopPropagation()

  dragMoved.value = false
  closeFloatingPanelsForDrag()

  // 获取触摸点坐标
  const touch = e.touches[0]
  isTouchDragging.value = true
  touchDragStart.y = touch.clientY

  animateBotScale(0.98, 0.15, 'power2.in')
}

// 拖拽中 - 只允许垂直方向拖动
const onDrag = (e) => {
  // 首先检查isDragging状态
  if (!isDragging.value || !botContainer.value) return

  // 防止事件冒泡和默认行为
  e.stopPropagation()
  e.preventDefault()

  // 计算垂直方向拖拽距离，忽略水平方向
  const deltaY = e.clientY - dragStart.y
  if (Math.abs(deltaY) > DRAG_MOVE_THRESHOLD) {
    dragMoved.value = true
  }

  // 只更新垂直位置
  position.y += deltaY

  // 应用约束条件
  updatePositionWithConstraints()

  // 更新拖拽起始位置（只更新y坐标，x坐标保持不变）
  dragStart.y = e.clientY
}

// 触摸拖拽中 - 只允许垂直方向拖动
const onTouchDrag = (e) => {
  // 首先检查isTouchDragging状态
  if (!isTouchDragging.value || !botContainer.value) return

  // 防止事件冒泡和默认行为
  e.stopPropagation()
  e.preventDefault()

  // 获取触摸点坐标
  const touch = e.touches[0]

  // 计算垂直方向拖拽距离，忽略水平方向
  const deltaY = touch.clientY - touchDragStart.y
  if (Math.abs(deltaY) > DRAG_MOVE_THRESHOLD) {
    dragMoved.value = true
  }

  // 只更新垂直位置
  position.y += deltaY

  // 应用约束条件
  updatePositionWithConstraints()

  // 更新拖拽起始位置（只更新y坐标）
  touchDragStart.y = touch.clientY
}

// 停止拖拽
const stopDrag = (e) => {
  // 防止事件冒泡和默认行为
  if (e) {
    e.stopPropagation()
    e.preventDefault()
  }

  // 移除全局事件监听
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)

  // 重置拖拽状态
  isDragging.value = false
  if (dragMoved.value) {
    suppressClickUntil.value = Date.now() + DRAG_CLICK_SUPPRESS_MS
  }

  animateBotScale(1, 0.2, 'back.out(1.2)')
}

// 停止触摸拖拽（移动端）
const stopTouchDrag = (e) => {
  // 防止事件冒泡
  if (e) {
    e.stopPropagation()
  }

  // 重置拖拽状态
  isTouchDragging.value = false
  if (dragMoved.value) {
    suppressClickUntil.value = Date.now() + DRAG_CLICK_SUPPRESS_MS
  }

  animateBotScale(1, 0.2, 'back.out(1.2)')
}

// 处理功能按钮点击
const handleFunction = (type) => {
  if (shouldSuppressClick() || type !== 'feedback') return
  openFeedbackModal()
}

const handleScroll = throttle(() => {
  const currentScrollY = window.scrollY
  const scrollDistance = Math.abs(currentScrollY - scrollCloseOriginY.value)

  if (scrollDistance >= SCROLL_CLOSE_THRESHOLD) {
    closeFloatingPanelsForScroll()
    scrollCloseOriginY.value = currentScrollY
  }

  const shouldShow = currentScrollY > BACK_TO_TOP_THRESHOLD
  if (shouldShow !== showBackToTop.value) {
    showBackToTop.value = shouldShow
    nextTick(updatePositionWithConstraints)
  }
}, 100)

// 窗口大小调整处理
const handleResize = () => {
  updatePositionWithConstraints()
}

// 组件挂载 - 仅在客户端执行
onMounted(() => {
  if (import.meta.client) {
    isChatbotUnmounted = false
    isDeviceReady.value = true
    scrollCloseOriginY.value = window.scrollY
    nextTick(() => {
      initPosition()
    })

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    document.addEventListener('touchmove', onTouchDrag, { passive: false })
    document.addEventListener('touchend', stopTouchDrag)
    document.addEventListener('touchcancel', stopTouchDrag)
  }
})

// 组件卸载 - 仅在客户端执行
onUnmounted(() => {
  if (import.meta.client) {
    isChatbotUnmounted = true
    if (botContainer.value) gsapInstance?.killTweensOf(botContainer.value)
    if (scrollCloseTimer.value) window.clearTimeout(scrollCloseTimer.value)

    // 移除事件监听
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDrag)
    document.removeEventListener('touchmove', onTouchDrag)
    document.removeEventListener('touchend', stopTouchDrag)
    document.removeEventListener('touchcancel', stopTouchDrag)
  }
})
</script>

<style lang="scss" scoped>
.customer-service-bot {
  position: fixed;
  z-index: $z-index-page-floating;
  overflow-anchor: none;
  transition:
    top 0.3s ease,
    right 0.3s ease;
  width: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: grab;

  &.is-dragging {
    cursor: grabbing;
    transition: none;

    :deep(.contact-popup-hover) {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
  }
}

.bot-buttons {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 52px;
  height: auto;
  background: $bg-white;
  padding: 4px;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba($color-black, 0.1);
  z-index: 1;
  opacity: 1;
  overflow: visible;
  pointer-events: auto;

  .bot-button-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 2px;
    width: 44px;
    min-height: 46px;
    padding: 4px;
    border-radius: 8px;
    cursor: pointer;
    transition:
      background 0.2s ease,
      color 0.2s ease;
    text-align: center;
    position: relative;
    text-decoration: none;
    color: $text-title-color;

    &:hover {
      background: $bg-color;

      .bot-button-icon {
        color: $text-title-color;
      }
    }

    &--service {
      min-height: 58px;
    }

    .bot-avatar {
      position: relative;
      width: 32px;
      height: 32px;
      border: 0.8px solid $bg-white;
      border-radius: 50%;
      overflow: hidden;
      flex: 0 0 auto;
    }

    .bot-icon {
      position: absolute;
      inset: -0.8px;
      width: 32px;
      height: 32px;
      display: block;
      object-fit: cover;
    }

    .bot-button-icon {
      width: 20px;
      height: 20px;
      color: $text-title-color;
      transition: color 0.2s ease;
    }

    span {
      font-size: 12px;
      color: $text-title-color;
      line-height: 1.4;
      white-space: nowrap;
    }
  }
}

.customer-service-bot.is-dragging {
  .bot-buttons,
  .bot-button-item,
  .bot-back-to-top {
    cursor: grabbing;
  }

  .bot-back-to-top:hover {
    opacity: 1;
    transform: none;
  }
}
</style>
